'use client';

import { useCallback, useContext, useEffect } from 'react';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import {
	BigNum,
	BASE_PRECISION_EXP,
	PRICE_PRECISION_EXP,
	QUOTE_PRECISION_EXP,
	ZERO,
} from '@velocity-exchange/sdk';
import { MarketOrderToastEventContext } from 'src/components/MarketOrderToasts/MarketOrderToastEventEmitterProvider';
import { MarketOrderToastOrderInfo } from 'src/components/MarketOrderToasts/MarketOrderToastStateTypes';
import useModal from 'src/components/Modals/hooks/useModal';
import useCurrentSettings from 'src/hooks/useCurrentSettings';

const usePnlModalDisplayListener = () => {
	const [savedSettings] = useCurrentSettings();
	const marketOrderToastEventContext = useContext(MarketOrderToastEventContext);
	const marketOrderToastEventsSubject = marketOrderToastEventContext.subject;
	const getStore = useDriftStore((s) => s.get);
	const { show } = useModal();

	const displayPnlModalForFilledOrders = useCallback(
		(props: Partial<MarketOrderToastOrderInfo>) => {
			const key = `${props.marketId?.marketIndex}-${props.orderId}`;
			// Get latest store value directly to avoid stale closure
			const ordersAwaitingPnlModalDisplay =
				getStore().ordersAwaitingPnlModalDisplay;
			const orderToDisplayInModal = ordersAwaitingPnlModalDisplay[key];
			if (orderToDisplayInModal) {
				const position = orderToDisplayInModal.openPositionInfo;
				const isIsolated = orderToDisplayInModal.isIsolated;

				// Only proceed if we have valid fill data
				if (
					!props.cumulativeQuoteAmountFilled ||
					!props.cumulativeBaseAmountFilled ||
					props.cumulativeQuoteAmountFilled.eq(ZERO) ||
					props.cumulativeBaseAmountFilled.eq(ZERO)
				) {
					console.warn('Invalid fill data, skipping PnL modal display');
					return;
				}

				const actualExitPrice = BigNum.from(
					props.cumulativeQuoteAmountFilled,
					QUOTE_PRECISION_EXP
				)
					.shift(BASE_PRECISION_EXP)
					.div(
						BigNum.from(props.cumulativeBaseAmountFilled, BASE_PRECISION_EXP)
					)
					.shiftTo(PRICE_PRECISION_EXP)
					.toNum();

				const baseAssetPriceDifference =
					position.direction === 'short'
						? (BigNum.from(
								position?.entryPrice,
								PRICE_PRECISION_EXP
							)?.toNum() ?? 0) - actualExitPrice
						: actualExitPrice -
							(BigNum.from(
								position?.entryPrice,
								PRICE_PRECISION_EXP
							)?.toNum() ?? 0);

				const pnlDollarValue =
					BigNum.from(
						props.cumulativeBaseAmountFilled,
						BASE_PRECISION_EXP
					).toNum() * baseAssetPriceDifference;

				if (pnlDollarValue > 0 && savedSettings.showPnlOnClose) {
					show({
						type: 'pnl_card',
						props: {
							position: {
								...position,
								quoteEntryAmount: props.cumulativeQuoteAmountFilled ?? ZERO,
								maxMarginRatio: position.maxMarginRatio,
							},
							actualExitPrice,
							pnl: pnlDollarValue,
							triggeredFrom: 'position_closed',
							isIsolated,
						},
					});
				}
			}
		},
		[getStore, savedSettings.showPnlOnClose, show]
	);

	useEffect(() => {
		const subscription = marketOrderToastEventsSubject.subscribe((event) => {
			if (event.type === 'order_completely_filled') {
				displayPnlModalForFilledOrders(event.data);
			}
		});

		return () => subscription.unsubscribe();
	}, [displayPnlModalForFilledOrders, marketOrderToastEventsSubject]);
};

export default usePnlModalDisplayListener;
